// src/components/Footer.jsx
import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  Text,
  Heading,
  Link as ChakraLink,
  Image,
  Separator,
  HStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

const tokens = {
  bg: { base: "white", _dark: "gray.900" },
  border: { base: "gray.200", _dark: "whiteAlpha.300" },
  heading: { base: "gray.800", _dark: "gray.100" },
  text: { base: "gray.700", _dark: "gray.300" },
  subtle: { base: "gray.600", _dark: "gray.400" },
};

function Col({ title, children }) {
  return (
    <Stack
      spacing={3}
      align={{ base: "center", md: "flex-start" }}
      textAlign={{ base: "center", md: "left" }}
    >
      <Heading as="h4" size="sm" color={tokens.heading}>
        {title}
      </Heading>
      <Stack spacing={2} fontSize="sm" color={tokens.text}>
        {children}
      </Stack>
    </Stack>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box
      as="footer"
      mt={16}
      borderTop="1px solid"
      borderColor={tokens.border}
      bg={tokens.bg}
    >
      <Container maxW="6xl" py={{ base: 8, md: 12 }}>
        {/* Brand + tagline */}
        <HStack
          mb={6}
          align="center"
          spacing={4}
          justify={{ base: "center", md: "flex-start" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Image
            src="/images/logo.png"
            alt="Consumer Advocate Reviews"
            h={{ base: "48px", md: "64px" }}
          />
          <Text fontSize={{ base: "md", md: "xl" }} color={tokens.subtle}>
            Accurate, Honest Decisions
          </Text>
        </HStack>

        {/* Centered 3-col grid (1/2/3 responsive) */}
        <SimpleGrid
          columns={{ base: 1, sm: 2, md: 3 }}
          spacing={{ base: 6, md: 8 }}
          justifyItems={{ base: "center", md: "start" }}
        >
          <Col title="How We Review">
            <ChakraLink as={RouterLink} to="/how-we-review">
              Our Methodology
            </ChakraLink>
            <ChakraLink as={RouterLink} to="/data-sources">
              Data & Sources
            </ChakraLink>
            <ChakraLink as={RouterLink} to="/about-us">
              About Us
            </ChakraLink>
          </Col>

          <Col title="Resources">
            <ChakraLink as={RouterLink} to="/tax-relief-companies">
              Top Companies
            </ChakraLink>
            <ChakraLink as={RouterLink} to="/tax-relief-blog">
              Blog & Guides
            </ChakraLink>
            <ChakraLink as={RouterLink} to="/tax-relief-calculator">
              Tax Relief Calculator
            </ChakraLink>
          </Col>

          <Col title="Legal">
            <ChakraLink as={RouterLink} to="/privacy-policy">
              Privacy Policy
            </ChakraLink>
            <ChakraLink as={RouterLink} to="/terms-of-use">
              Terms of Use
            </ChakraLink>
            <ChakraLink as={RouterLink} to="/advertiser-disclosure">
              Advertiser Disclosure
            </ChakraLink>
          </Col>
        </SimpleGrid>

        <Separator my={6} borderColor={tokens.border} />

        {/* Disclaimers */}
        <Stack
          spacing={3}
          fontSize="xs"
          color={tokens.subtle}
          textAlign={{ base: "center", md: "left" }}
        >
          <Text>
            Consumer Advocate Reviews is an independent information resource.
            Content is for educational purposes only and does not constitute tax
            or legal advice. Outcomes vary by individual circumstances; no
            company can guarantee specific results.
          </Text>
          <Text>
            We may receive compensation from some partners when you click links
            or call numbers on this site. Our editorial opinions are our own.
          </Text>
        </Stack>

        <Separator my={6} borderColor={tokens.border} />

        {/* Bottom bar */}
        <HStack
          justify={{ base: "center", md: "space-between" }}
          align="center"
          flexWrap="wrap"
          gap={3}
          textAlign={{ base: "center", md: "left" }}
        >
          <Text fontSize="sm" color={tokens.subtle}>
            © {year} Consumer Advocate Reviews. All rights reserved.
          </Text>
        </HStack>
      </Container>
    </Box>
  );
}
